import React, { useMemo, useRef, useEffect } from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import JobMapPopup from './JobMapPopup';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Create custom icon for job markers with heat map styling
const createJobIcon = (count = 1) => {
  let size, backgroundColor, fontSize;
  
  // Heat map style based on job count - optimized for mobile touch
  if (count >= 50) {
    size = 56;
    backgroundColor = '#d73027'; // Dark red
    fontSize = '15px';
  } else if (count >= 20) {
    size = 48;
    backgroundColor = '#f46d43'; // Orange-red
    fontSize = '14px';
  } else if (count >= 10) {
    size = 42;
    backgroundColor = '#fdae61'; // Orange
    fontSize = '13px';
  } else if (count >= 5) {
    size = 36;
    backgroundColor = '#fee08b'; // Yellow-orange
    fontSize = '12px';
  } else if (count > 1) {
    size = 32;
    backgroundColor = '#e6f598'; // Light green
    fontSize = '11px';
  } else {
    size = 28;
    backgroundColor = '#3288bd'; // Blue for single jobs
    fontSize = '10px';
  }
  
  return L.divIcon({
    html: `
      <div style="
        background-color: ${backgroundColor};
        color: ${count > 4 ? 'white' : 'black'};
        border-radius: 50%;
        width: ${size}px;
        height: ${size}px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: ${fontSize};
        border: 2px solid white;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        cursor: pointer;
      ">
        ${count}
      </div>
    `,
    className: 'heat-map-marker',
    iconSize: [size, size],
    iconAnchor: [size/2, size/2],
    popupAnchor: [0, -size/2]
  });
};

// Function to extract coordinates from job Geolocation field
const getJobCoordinates = (job) => {
  // Try to get coordinates from the Geolocation field
  if (job.Geolocation) {
    try {
      // Handle if Geolocation is already parsed object
      if (typeof job.Geolocation === 'object' && job.Geolocation.latitude && job.Geolocation.longitude) {
        const lat = parseFloat(job.Geolocation.latitude);
        const lng = parseFloat(job.Geolocation.longitude);
        
        // Validate coordinates are reasonable for US
        if (!isNaN(lat) && !isNaN(lng) && lat >= 20 && lat <= 72 && lng >= -180 && lng <= -60) {
          return [lat, lng];
        }
      }
      
      // Handle if Geolocation is JSON string
      if (typeof job.Geolocation === 'string') {
        const parsed = JSON.parse(job.Geolocation);
        if (parsed.latitude && parsed.longitude) {
          const lat = parseFloat(parsed.latitude);
          const lng = parseFloat(parsed.longitude);
          
          // Validate coordinates are reasonable for US
          if (!isNaN(lat) && !isNaN(lng) && lat >= 20 && lat <= 72 && lng >= -180 && lng <= -60) {
            return [lat, lng];
          }
        }
      }
    } catch (error) {
      console.warn('Failed to parse Geolocation for job:', job.Record_Id, error);
    }
  }
  
  // If no valid Geolocation, return null (job won't be shown on map)
  console.warn('No valid Geolocation found for job:', job.Record_Id, 'Location:', job.city, job.State);
  return null;
};

// Component to handle individual marker with job data
const JobMarker = ({ location, index }) => {
  const markerRef = useRef(null);
  const jobCount = location.jobs.length;
  
  useEffect(() => {
    if (markerRef.current) {
      // Attach job data directly to the Leaflet marker instance
      const marker = markerRef.current;
      marker.jobCount = jobCount;
      marker.jobs = location.jobs;
      marker.options.jobCount = jobCount;
      marker.options.jobs = location.jobs;
    }
  }, [jobCount, location.jobs]);
  
  return (
    <Marker
      ref={markerRef}
      key={`marker-location-${index}`}
      position={location.position}
      icon={createJobIcon(jobCount)}
    >
      <Popup maxWidth={350} maxHeight={400}>
        {jobCount === 1 ? (
          <JobMapPopup job={location.jobs[0]} />
        ) : (
          <div style={{ minWidth: '250px' }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#28346c' }}>
              {jobCount} Jobs in {location.city}, {location.state}
            </h3>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {location.jobs.map((job, jobIndex) => (
                <div 
                  key={`job-${jobIndex}`}
                  style={{ 
                    padding: '8px 0', 
                    borderBottom: jobIndex < location.jobs.length - 1 ? '1px solid #eee' : 'none'
                  }}
                >
                  <JobMapPopup job={job} />
                </div>
              ))}
            </div>
          </div>
        )}
      </Popup>
    </Marker>
  );
};

const JobMapMarkers = ({ jobs }) => {
  const markersData = useMemo(() => {
    if (!jobs || jobs.length === 0) {
      return [];
    }

    // Group jobs by city/state TEXT first, not coordinates
    const locationGroups = {};
    const cityCoordinates = {}; // Store first coordinate found for each city
    
    jobs.forEach(job => {
      const coords = getJobCoordinates(job);
      if (!coords) return;
      
      // Create key from city and state text (normalized)
      const city = (job.city || '').trim().toLowerCase();
      const state = (job.State || '').trim().toUpperCase();
      
      if (!city || !state) return; // Skip if missing city/state
      
      const locationKey = `${city}, ${state}`;
      
      // If this is the first job for this city, store its coordinates
      if (!cityCoordinates[locationKey]) {
        cityCoordinates[locationKey] = coords;
      }
      
      // Group all jobs by city/state text
      if (!locationGroups[locationKey]) {
        locationGroups[locationKey] = {
          position: cityCoordinates[locationKey], // Use consistent coordinates
          jobs: [],
          city: job.city, // Keep original case
          state: job.State
        };
      }
      
      locationGroups[locationKey].jobs.push(job);
    });
    
    // Ensure all jobs in same city use the same coordinates
    Object.values(locationGroups).forEach(group => {
      const key = `${group.city.toLowerCase()}, ${group.state}`;
      group.position = cityCoordinates[key];
    });
    
    // Return location groups as markers
    const markers = Object.values(locationGroups);
    return markers;
  }, [jobs]);

  return (
    <>
      {markersData.map((location, index) => (
        <JobMarker key={`job-marker-${index}`} location={location} index={index} />
      ))}
    </>
  );
};

export default JobMapMarkers;